import React, { useMemo, useRef } from "react";
import "./Timetable.css";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { ArrowBack } from "@mui/icons-material";
import { Button } from "@mui/material";
import { printHtml } from "../../utils/htmlToPrint";

/* ---------- CONFIG ---------- */
const STANDARD_ALIAS = {
  Nursary: "Nursary",
  LKG: "KG-1",
  UKG: "KG-2",
  "1st": "Class-1",
  "2nd": "Class-2",
  "3rd": "Class-3",
  "4th": "Class-4",
  "5th": "Class-5",
  "6th": "Class-6",
  "7th": "Class-7",
  "8th": "Class-8",
  "9th": "Class-9",
  "10th": "Class-10",
};

// Explicit order so "1st, 2nd ... 10th" sort correctly (string sort fails)
const STANDARD_ORDER = [
  "Nursary", "LKG", "UKG",
  "1st", "2nd", "3rd", "4th", "5th",
  "6th", "7th", "8th", "9th", "10th",
];

const normalizeStandard = (std = "") => String(std).trim();

const standardRank = (std) => {
  const idx = STANDARD_ORDER.indexOf(normalizeStandard(std));
  return idx === -1 ? 999 : idx;
};

const DAY_ORDER = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];

/* ---------- HELPERS ---------- */
const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

/* ---------- COMPONENT ---------- */
export default function Timetable() {
  const navigate = useNavigate();
  const location = useLocation();
  const { entryCards = [], school = {} } = location.state || {};
  const printRef = useRef(null);

  /* ---------- DYNAMIC TABLES ---------- */
  const TABLES = useMemo(() => {
    const standards = Array.from(
      new Set(entryCards.map((r) => normalizeStandard(r.standard)))
    )
      .filter(Boolean)
      .sort((a, b) => standardRank(a) - standardRank(b));

    if (standards.length === 0) return [];

    const mid = Math.ceil(standards.length / 2);

    return [
      { key: "primary", standards: standards.slice(0, mid) },
      { key: "middleHigh", standards: standards.slice(mid) },
    ];
  }, [entryCards]);

  /* ---------- ROW DATA (per table) ---------- */
  const tableData = useMemo(() => {
    // Group: standard → date → { subject, days }
    const byStandard = {};

    entryCards.forEach((row) => {
      const std = normalizeStandard(row.standard);
      const date = row.examDate;
      if (!std || !date) return;

      if (!byStandard[std]) byStandard[std] = {};
      if (!byStandard[std][date]) {
        byStandard[std][date] = { subject: row.subject, days: row.days };
      }
    });

    return TABLES.map((table) => {
      const dateSet = new Set();
      table.standards.forEach((std) => {
        Object.keys(byStandard[std] || {}).forEach((d) => dateSet.add(d));
      });

      const rows = [...dateSet]
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          let dayName = "";
          for (const std of table.standards) {
            const cell = byStandard[std]?.[date];
            if (cell?.days) {
              dayName = cell.days;
              break;
            }
          }
          if (!dayName) {
            dayName = DAY_ORDER[new Date(date).getDay()] || "";
          }

          return {
            date,
            days: dayName,
            subjects: table.standards.reduce((acc, std) => {
              acc[std] = byStandard[std]?.[date]?.subject || "";
              return acc;
            }, {}),
          };
        });

      return { ...table, rows };
    });
  }, [entryCards, TABLES]);

  /* ---------- PRINT ---------- */
  const handlePrint = () => {
    printHtml('printRef');
    window.location.reload();
    // window.print();
  }

  /* ---------- DOWNLOAD EXCEL ---------- */
  const handleDownload = () => {
    const hasData = tableData.some((t) => t.rows.length);
    if (!hasData) {
      alert("No data to export.");
      return;
    }

    const wb = XLSX.utils.book_new();

    tableData.forEach((table, idx) => {
      const header = [
        "Date",
        "Days",
        ...table.standards.map((s) => STANDARD_ALIAS[s] || s),
      ];

      const dataRows = table.rows.map((row) => [
        formatDate(row.date),
        row.days,
        ...table.standards.map((std) => row.subjects[std] || ""),
      ]);

      const metaRows = [
        [school?.schoolName || "Exam Timetable"],
        [
          `Examination Schedule: ${entryCards[0]?.academicYear || ""}`,
          "",
          `${entryCards[0]?.startTime || ""} - ${entryCards[0]?.endTime || ""}`,
        ],
        [],
      ];

      const ws = XLSX.utils.aoa_to_sheet([
        ...metaRows,
        header,
        ...dataRows,
      ]);

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
      ];
      ws["!cols"] = header.map(() => ({ wch: 18 }));

      const sheetName =
        idx === 0 ? "Primary" : idx === 1 ? "Middle-High" : `Table-${idx + 1}`;

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const year = entryCards[0]?.academicYear || "Timetable";
    XLSX.writeFile(wb, `Exam-Timetable-${year}.xlsx`);
  };

  if (!entryCards.length) {
    return <p style={{ padding: 20 }}>No exam entries found.</p>;
  }

  return (
    <div className="timetable-page">
      <div className="action-bar no-print">
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <button 
          className="btn btn-print" onClick={handlePrint}>
          🖨️ Print
        </button>
        <button className="btn btn-print" onClick={handleDownload}>
          ⬇️ Download Excel
        </button>
      </div>

      <div className="timetable-wrapper" ref={printRef} id="printRef">
        {tableData.map((table) => (
          <div className="table-block" key={table.key}>
            <h1 className="school-title">
              {school?.schoolName || "LIVING HOPE ENGLISH MEDIUM SCHOOL"}
            </h1>

            <div className="meta-center">
              {[
                school?.adaddress?.addressLine1,
                school?.adaddress?.city,
                school?.adaddress?.state,
              ]
                .filter(Boolean)
                .join(", ")}
            </div>

            <div className="meta-row">
              <span className="meta-left">
                Examination Schedule: {entryCards[0].academicYear || ""}
              </span>
              <span className="meta-center">
                {entryCards[0]?.startTime || ""} -{" "}
                {entryCards[0]?.endTime || ""}
              </span>
              <span className="meta-right">TIME -TABLE</span>
            </div>

            {table.rows.length === 0 ? (
              <p className="empty-note">
                No exams scheduled for this section.
              </p>
            ) : (
              <table className="timetable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Days</th>
                    {table.standards.map((std) => (
                      <th key={std}>{STANDARD_ALIAS[std] || std}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.date}>
                      <td className="date-cell">{formatDate(row.date)}</td>
                      <td>{row.days}</td>
                      {table.standards.map((std) => (
                        <td key={std}>{row.subjects[std] || ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}